/**
 * Currency utilities for locale-based currency handling
 * Maps locales to their corresponding currency codes and symbols
 */

import { numberFormats } from "~/locales/formats";

/**
 * Get currency code for a given locale
 * @param {string} locale - Locale code (e.g., 'hu', 'cs', 'de', 'en')
 * @returns {string} Currency code (e.g., 'HUF', 'CZK', 'EUR')
 */
export function getCurrencyCode(locale) {
  const formats = numberFormats[locale];
  if (!formats || !formats.currency) {
    // Fallback to HUF if locale not found
    return "HUF";
  }
  return formats.currency.currency;
}

/**
 * Get currency symbol for a given locale using Intl.NumberFormat
 * @param {string} locale - Locale code (e.g., 'hu', 'cs', 'de', 'en')
 * @returns {string} Currency symbol (e.g., 'Ft', 'Kč', '€')
 */
export function getCurrencySymbol(locale) {
  const code = getCurrencyCode(locale);

  try {
    // Use Intl.NumberFormat to extract the currency symbol
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: "symbol",
    });

    // Format a small number and extract just the symbol
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find((part) => part.type === "currency");

    return symbolPart ? symbolPart.value : code;
  } catch (error) {
    // Fallback to currency code if formatting fails
    return code;
  }
}

/**
 * Composable for accessing currency information in Vue components
 * @returns {Object} Currency utilities
 */
export default function useCurrency() {
  return {
    getCurrencyCode,
    getCurrencySymbol,
  };
}
